import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useParams,
} from 'react-router-dom';
import BreadcrumbsHomeIcon from '@/assets/breadcrumbs-home.svg';
import OrganisationsIcon from '@/assets/organisations.svg';
import { breadcrumbTitleKeys } from '@/breadcrumbs';
import { RouteErrorFallback } from '@/components/ui/route-error-fallback';
import { urls } from '@/constants/urls';
import { AuthenticatedLayout } from '@/layouts/authenticated-layout';
import { ApiCheckPage } from '@/pages/api-check/api-check-page';
import { AccountPage } from '@/pages/authenticated/account/account-page';
import { CredentialsPage } from '@/pages/authenticated/credentials/credentials-page';
import { DashboardPage } from '@/pages/authenticated/dashboard/dashboard-page';
import { DevicesPage } from '@/pages/authenticated/devices/devices-page';
import { OrganisationDevicesPage } from '@/pages/authenticated/devices/organisation-devices-page';
import AssociateUnitsPage from '@/pages/authenticated/groups/group/associate-units/page-associate-units';
import { GroupPage } from '@/pages/authenticated/groups/group/group-page';
import { NewUnitPage } from '@/pages/authenticated/groups/group/new-unit/new-unit-page';
import { UnitPage } from '@/pages/authenticated/groups/group/unit/unit-page';
import { GroupsPage } from '@/pages/authenticated/groups/groups-page';
import { HomePage } from '@/pages/authenticated/home/home-page';
import { ImportingPage } from '@/pages/authenticated/importing/page-importing';
import { OrganisationsPage } from '@/pages/authenticated/organisations/organisations-page';
import { OrganisationPoliciesPage } from '@/pages/authenticated/policies/organisation-policies-page';
import { PoliciesPage } from '@/pages/authenticated/policies/policies-page';
import { UnitsPage } from '@/pages/authenticated/units/units-page';
import { UsersPage } from '@/pages/authenticated/users/users-page';
import { LoginPage } from '@/pages/unauthenticated/login/login-page';
import { RouteHandleBreadcrumb } from '@/types/route-handle-breadcrumb';
import { RouteHandleSidebar } from '@/types/route-handle-sidebar';
import { AuthenticatedRouteWrapper } from './authenticated-route-wrapper';
import { GroupRouteWrapper } from './group-route-wrapper';
import { NestedOrganisationNavLinks } from './nested-organisations-nav-links';
import { PlatformAdminAndOrganisationAdminRouteWrapper } from './platform-admin-and-organisation-admin-route-wrapper';
import { PlatformAdminOrganisationOverrideRouteWrapper } from './platform-admin-organisation-override-route-wrapper';
import { PlatformAdminRouteWrapper } from './platform-admin-route-wrapper';
import { UnitRouteWrapper } from './unit-route-wrapper';

export const router = createBrowserRouter([
  {
    path: '/api-check/:path',
    element: <ApiCheckPage />,
  },
  {
    path: urls.home,
    element: (
      <AuthenticatedRouteWrapper>
        <AuthenticatedLayout>
          <Outlet />
        </AuthenticatedLayout>
      </AuthenticatedRouteWrapper>
    ),
    errorElement: (
      <AuthenticatedRouteWrapper>
        <AuthenticatedLayout>
          <RouteErrorFallback />
        </AuthenticatedLayout>
      </AuthenticatedRouteWrapper>
    ),
    handle: {
      crumb: {
        display: <BreadcrumbsHomeIcon />,
      } satisfies RouteHandleBreadcrumb,
    },
    children: [
      {
        path: urls.home,
        element: <HomePage />,
      },
      {
        path: urls.importing.base,
        element: (
          <PlatformAdminRouteWrapper>
            <ImportingPage />
          </PlatformAdminRouteWrapper>
        ),
      },
      {
        path: urls.account,
        element: <AccountPage />,
        handle: {
          crumb: {
            display: 'Account',
          } satisfies RouteHandleBreadcrumb,
        },
      },
      {
        path: urls.credentials.base,
        element: (
          <PlatformAdminRouteWrapper>
            <CredentialsPage />
          </PlatformAdminRouteWrapper>
        ),
      },
      {
        path: urls.organisations.base,
        element: (
          <PlatformAdminRouteWrapper>
            <Outlet />
          </PlatformAdminRouteWrapper>
        ),
        children: [
          {
            path: urls.organisations.base,
            element: <OrganisationsPage />,
          },
          {
            path: urls.organisations.organisation.base.path,
            element: (
              <PlatformAdminOrganisationOverrideRouteWrapper
                fallback={urls.organisations.base}
              >
                <Outlet />
              </PlatformAdminOrganisationOverrideRouteWrapper>
            ),
            handle: {
              sidebar: {
                nested: {
                  title: 'ORGANISATIONS',
                  Icon: OrganisationsIcon,
                  content: <NestedOrganisationNavLinks />,
                },
              } satisfies RouteHandleSidebar,
              crumb: {
                display: <BreadcrumbsHomeIcon />,
                isRoot: true,
              } satisfies RouteHandleBreadcrumb,
            },
            children: [
              {
                path: urls.organisations.organisation.base.path,
                element: <HomePage />,
              },
              {
                path: urls.organisations.organisation.groups.base.path,
                element: <Outlet />,
                handle: {
                  crumb: {
                    display: 'Groups',
                  } satisfies RouteHandleBreadcrumb,
                },
                children: [
                  {
                    path: urls.organisations.organisation.groups.base.path,
                    element: <GroupsPage />,
                  },
                  {
                    path: urls.organisations.organisation.groups.group.base
                      .path,
                    element: (
                      <GroupRouteWrapper>
                        <Outlet />
                      </GroupRouteWrapper>
                    ),
                    handle: {
                      crumb: {
                        key: breadcrumbTitleKeys.currentGroupName,
                      } satisfies RouteHandleBreadcrumb,
                    },
                    children: [
                      {
                        path: urls.organisations.organisation.groups.group.base
                          .path,
                        element: <GroupPage />,
                      },
                      {
                        path: urls.organisations.organisation.groups.group
                          .new_unit.path,
                        element: <NewUnitPage />,
                        handle: {
                          crumb: { hide: true } satisfies RouteHandleBreadcrumb,
                        },
                      },
                      {
                        path: `${urls.organisations.organisation.groups.group.base.path}/associate-units`,
                        element: <AssociateUnitsPage />,
                      },
                      {
                        path: urls.organisations.organisation.groups.group.unit
                          .path,
                        element: (
                          <UnitRouteWrapper>
                            <UnitPage />
                          </UnitRouteWrapper>
                        ),
                        handle: {
                          crumb: {
                            key: breadcrumbTitleKeys.currentUnitName,
                          } satisfies RouteHandleBreadcrumb,
                        },
                      },
                      {
                        path: `${urls.organisations.organisation.groups.group.base.path}/*`,
                        Component: () => {
                          const { orgId, groupId } = useParams<{
                            orgId: string;
                            groupId: string;
                          }>();
                          return (
                            <Navigate
                              to={urls.organisations.organisation.groups.group.base.compose(
                                parseInt(orgId ?? ''),
                                parseInt(groupId ?? ''),
                              )}
                            />
                          );
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: urls.organisations.organisation.units.path,
                element: <UnitsPage />,
              },
              {
                path: urls.organisations.organisation.devices.path,
                element: <OrganisationDevicesPage />,
              },
              {
                path: urls.organisations.organisation.policies.path,
                element: <OrganisationPoliciesPage />,
              },
              {
                path: urls.organisations.organisation.reporting.path,
                element: <DashboardPage />,
              },
              {
                path: urls.organisations.organisation.users.optionalUser,
                element: <UsersPage />,
              },
              {
                path: `${urls.organisations.organisation.base.path}/*`,
                Component: () => {
                  const { id } = useParams<{ id: string }>();
                  return (
                    <Navigate
                      to={urls.organisations.organisation.base.compose(
                        parseInt(id ?? ''),
                      )}
                    />
                  );
                },
              },
            ],
          },
        ],
      },
      {
        path: urls.groups.base,
        element: <Outlet />,
        handle: {
          crumb: {
            display: 'Groups',
          } satisfies RouteHandleBreadcrumb,
        },
        children: [
          {
            path: urls.groups.base,
            element: <GroupsPage />,
          },
          {
            path: urls.groups.group.base.path,
            element: (
              <GroupRouteWrapper>
                <Outlet />
              </GroupRouteWrapper>
            ),
            handle: {
              crumb: {
                key: breadcrumbTitleKeys.currentGroupName,
              } satisfies RouteHandleBreadcrumb,
            },
            children: [
              {
                path: urls.groups.group.base.path,
                element: <GroupPage />,
              },
              {
                path: urls.groups.group.new_unit.path,
                element: <NewUnitPage />,
              },
              {
                path: urls.groups.group.unit.path,
                element: (
                  <UnitRouteWrapper>
                    <UnitPage />
                  </UnitRouteWrapper>
                ),
                handle: {
                  crumb: {
                    key: breadcrumbTitleKeys.currentUnitName,
                  } satisfies RouteHandleBreadcrumb,
                },
              },
              {
                path: `${urls.groups.group.base.path}/*`,
                Component: () => {
                  const { groupId } = useParams<{
                    groupId: string;
                  }>();
                  return (
                    <Navigate
                      to={urls.groups.group.base.compose(
                        parseInt(groupId ?? ''),
                      )}
                    />
                  );
                },
              },
            ],
          },
        ],
      },
      {
        path: urls.devices.base,
        element: <DevicesPage />,
      },
      {
        path: urls.units.base,
        element: <UnitsPage />,
        handle: {
          crumb: {
            display: 'Units',
          } satisfies RouteHandleBreadcrumb,
        },
      },
      {
        path: urls.policies.base,
        element: (
          <PlatformAdminAndOrganisationAdminRouteWrapper>
            <Outlet />
          </PlatformAdminAndOrganisationAdminRouteWrapper>
        ),
        handle: {
          crumb: {
            display: 'Policies',
          } satisfies RouteHandleBreadcrumb,
        },
        children: [
          {
            path: urls.policies.base,
            element: <PoliciesPage />,
            handle: {
              crumb: {
                hide: true,
              } satisfies RouteHandleBreadcrumb,
            },
          },
          {
            path: urls.policies.organisation.path,
            element: (
              <PlatformAdminOrganisationOverrideRouteWrapper
                fallback={urls.policies.base}
              >
                <OrganisationPoliciesPage />
              </PlatformAdminOrganisationOverrideRouteWrapper>
            ),
            handle: {
              crumb: {
                key: breadcrumbTitleKeys.currentOrganisationName,
              } satisfies RouteHandleBreadcrumb,
            },
          },
        ],
      },
      {
        path: urls.users.base,
        element: (
          <PlatformAdminAndOrganisationAdminRouteWrapper>
            <Outlet />
          </PlatformAdminAndOrganisationAdminRouteWrapper>
        ),
        children: [
          {
            path: urls.users.optionalUser,
            element: <UsersPage />,
          },
        ],
      },
      {
        path: urls.reporting.base,
        element: <DashboardPage />,
      },
    ],
  },
  {
    path: urls.login,
    element: <LoginPage />,
  },
  {
    path: '/*',
    element: <Navigate to={urls.home} />,
  },
]);
